import React from "react";
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useSchedule } from '../../hooks/useSchedule';
import { D3GanttChart, D3GanttItem } from './D3GanttChart';

interface DependenciesGanttChartProps {
  dependencyNodeId: wasm.DependencyNodeId;
  title?: string;
}

export type DependenciesGanttItemMetadata = {
  source?: wasm.ResourceAllocationSource;
};

/**
 * A specialized Gantt chart that displays a node and its dependencies
 */
export function DependenciesGanttChart({ dependencyNodeId, title }: DependenciesGanttChartProps) {
  const { database, dependencyGraph } = useDatabase();
  const { schedule } = useSchedule();

  // Process the dependency node and generate Gantt items
  const { items, description, chartTitle, epochs, currentEpochIndex, currentEpochPosition } = React.useMemo(() => {
    // Default values
    const result = {
      items: [] as D3GanttItem<DependenciesGanttItemMetadata>[],
      description: "",
      chartTitle: title || "Dependencies Timeline",
      epochs: [] as { id: string; label: string }[],
      currentEpochIndex: undefined as number | undefined,
      currentEpochPosition: undefined as number | undefined,
    };

    // Check if we have all the required data.
    // The schedule is now a fuzzy schedule DTO (plain object), not the old
    // WrappedSchedule class. This component was designed for the old epoch-based
    // schedule and needs a rewrite to work with fuzzy distributions.
    // For now, return empty — the Forecast view handles fuzzy schedule visualization.
    if (!database || !schedule || !dependencyGraph) {
      return { ...result, description: "Loading data..." };
    }
    if (typeof (schedule as any).ordered_epochs !== 'function') {
      return { ...result, description: "Schedule visualization is available in the Forecast view." };
    }

    // Determine node type and ID
    let nodeType: 'Task' | 'Outcome' | null = null;
    let nodeId: string | null = null;

    if ('Task' in dependencyNodeId) {
      nodeType = 'Task';
      nodeId = (dependencyNodeId as { Task: string }).Task;
    } else if ('Outcome' in dependencyNodeId) {
      nodeType = 'Outcome';
      nodeId = (dependencyNodeId as { Outcome: string }).Outcome;
    }

    if (!nodeType || !nodeId) {
      return { ...result, description: "Invalid dependency node ID provided" };
    }

    // Get the node data based on its type
    let nodeName = "";

    if (nodeType === 'Task') {
      const tasks = database.tasks();
      const task = tasks[nodeId];
      if (!task) {
        return { ...result, description: "Task not found" };
      }
      nodeName = task.title();
    } else { // Outcome
      const outcomes = database.outcomes();
      const outcome = outcomes[nodeId];
      if (!outcome) {
        return { ...result, description: "Outcome not found" };
      }
      nodeName = outcome.title();
    }

    // Update the title with the node name
    const updatedTitle = title || `Dependencies: ${nodeName}`;

    // Get all dependencies
    const dependencies = dependencyGraph.get_deep_dependencies(dependencyNodeId);

    // Extract task IDs from dependency nodes
    const dependencyTaskIds = dependencies
      .filter((dep: any) => 'Task' in dep)
      .map((dep: any) => (dep as { Task: string }).Task);

    console.log("dependencyTaskIds", dependencyTaskIds);

    // Extract outcome IDs from dependency nodes
    const dependencyOutcomeIds = dependencies
      .filter((dep: any) => 'Outcome' in dep)
      .map((dep: any) => (dep as { Outcome: string }).Outcome);

    // Create Gantt items
    const ganttItems: D3GanttItem<DependenciesGanttItemMetadata>[] = [];
    const tasks = database.tasks();
    const outcomes = database.outcomes();

    // Parse SchedulingEpoch to start/end timestamps (ms) with type guards
    function epochToRangeMs(epoch: any): { start: number; end: number } | null {
      const e = (typeof epoch.inner === 'function') ? epoch.inner() : epoch;
      if (e && e.type === 'Day' && typeof e.day === 'string') {
        const start = new Date(e.day + 'T00:00:00Z').getTime();
        const end = start + 24 * 60 * 60 * 1000;
        return { start, end };
      }
      if (e && e.type === 'Days' && typeof e.start_inclusive === 'string' && typeof e.end_inclusive === 'string') {
        const start = new Date(e.start_inclusive + 'T00:00:00Z').getTime();
        const end = new Date(e.end_inclusive + 'T00:00:00Z').getTime() + 24 * 60 * 60 * 1000;
        return { start, end };
      }
      return null;
    }

    // Aggregate allocations for a task into contiguous spans
    function allocationsToSpans(allocations: wasm.WrappedResourceAllocation[]): Array<{ start: number; end: number }> {
      const ranges: Array<{ start: number; end: number }> = [];
      for (const allocation of allocations) {
        const epoch = allocation.when();
        const r = epochToRangeMs(epoch);
        if (r) ranges.push(r);
      }
      // sort and merge overlapping/contiguous ranges
      ranges.sort((a,b)=> a.start - b.start);
      const merged: Array<{ start: number; end: number }> = [];
      for (const r of ranges) {
        if (merged.length === 0) { merged.push({ ...r }); continue; }
        const last = merged[merged.length-1];
        if (r.start <= last.end) { // overlap or touch
          last.end = Math.max(last.end, r.end);
        } else {
          merged.push({ ...r });
        }
      }
      return merged;
    }

    // Build future epoch window and mapping to categorical indices
    const nowMs = Date.now();
    const allEpochs = schedule.ordered_epochs();
    const futureEpochs: { id: string; label: string; startMs: number; endMs: number; key: string }[] = [];
    const keyFor = (inner: any): string => inner.type === 'Day' ? `D:${inner.day}` : inner.type === 'Days' ? `R:${inner.start_inclusive}:${inner.end_inclusive}` : '';
    for (const w of allEpochs) {
      const e = w.inner();
      const rng = epochToRangeMs(e);
      if (!rng) continue;
      if (rng.end < nowMs) continue;
      const key = keyFor(e);
      const label = new Date(rng.start).toLocaleDateString(undefined,{ month:'numeric', day:'numeric'});
      futureEpochs.push({ id: key || String(futureEpochs.length), label, startMs: rng.start, endMs: rng.end, key });
    }
    const MAX_EPOCHS = 26;
    const windowEpochs = futureEpochs.slice(0, MAX_EPOCHS);
    const epochs = windowEpochs.map(e => ({ id: e.id, label: e.label }));
    const indexByKey = new Map<string, number>();
    windowEpochs.forEach((e, i) => indexByKey.set(e.key, i));
    const cur = schedule.current_epoch();
    let currentEpochIndex: number | undefined = undefined;
    let currentEpochPosition: number | undefined = undefined;
    if (cur) {
      const inner = cur.inner();
      const rng = epochToRangeMs(inner);
      if (rng) {
        currentEpochIndex = windowEpochs.findIndex(e => rng.start >= e.startMs && rng.start < e.endMs);
        if (currentEpochIndex >= 0) {
          const e = windowEpochs[currentEpochIndex];
          const frac = Math.min(1, Math.max(0, (nowMs - e.startMs) / (e.endMs - e.startMs)));
          currentEpochPosition = currentEpochIndex + frac;
        }
      }
    }

    // Helper function to add a task to gantt items
    function addTaskToGanttItems(
      id: string,
      tasks: Record<string, wasm.WrappedTask>,
      items: D3GanttItem<DependenciesGanttItemMetadata>[]
    ): void {
      const task = tasks[id];
      if (!task) return;

      // Get allocations for this task using the allocations_by_task method
      const allAllocations = schedule?.allocations_by_task();
      const taskAllocations = allAllocations?.[id] || [];

      // Aggregate into spans to produce readable bars
      const spans = allocationsToSpans(taskAllocations);
      spans.forEach((span, index) => {
        if (span.end <= nowMs) return;
        const sKey = windowEpochs.find(e => span.start >= e.startMs && span.start < e.endMs)?.key
          ?? windowEpochs.find(e => e.startMs >= span.start)?.key;
        let eKey = windowEpochs.find(e => span.end > e.startMs && span.end <= e.endMs)?.key;
        if (!eKey) {
          for (let i = windowEpochs.length - 1; i >= 0; i--) {
            if (windowEpochs[i].endMs <= span.end) { eKey = windowEpochs[i].key; break; }
          }
        }
        if (!sKey || !eKey) return;
        const sIdx = indexByKey.get(sKey);
        const eIdx = indexByKey.get(eKey);
        if (sIdx == null || eIdx == null) return;
        items.push({
          id: `task-${id}-span-${index}`,
          label: task.title(),
          startDate: new Date(sIdx),
          endDate: new Date(eIdx),
          metadata: {},
        });
      });
    }

    // Add the main node if it's a Task (Outcomes themselves don't have allocations)
    if (nodeType === 'Task') {
      addTaskToGanttItems(nodeId, tasks, ganttItems);
    }

    // Add task dependencies (for Outcomes, these are the tasks that contribute)
    dependencyTaskIds.forEach((id: string) => {
      addTaskToGanttItems(id, tasks, ganttItems);
    });

    // Only scheduled items are rendered in the Gantt; no unscheduled placeholders

    // console.log("ganttItems", ganttItems);

    // Add outcome dependencies (as milestones)
    // TODO restore this in some way that makes sense. Maybe just as a marker
    // on completion
    // dependencyOutcomeIds.forEach((id: string) => {
    //   const outcome = outcomes[id];
    //   if (!outcome) return;

    //   // Add outcome as a milestone with estimated date
    //   ganttItems.push({
    //     id: `outcome-${id}`,
    //     label: outcome.title(),
    //     startDate: new Date(), // Use current date as placeholder
    //     // TODO this is broken
    //     endDate: new Date(Date.now() + 24 * 60 * 60 * 1000), // 1 day duration
    //     metadata: {}
    //   });
    // });

    return {
      items: ganttItems,
      description: ganttItems.length === 0 ? "No scheduled items found" : "",
      chartTitle: updatedTitle,
      epochs,
      currentEpochIndex,
      currentEpochPosition,
    };
  }, [database, dependencyGraph, dependencyNodeId, schedule, title]);

  // Render the Gantt chart with the computed items
  // Hide legend if there is only a single group (or none)
  const uniqueGroups = Array.from(new Set(items.map(i => i.groupId || ''))).filter(Boolean);
  const legendMode = uniqueGroups.length > 1 ? 'group' : 'none';

  return (
    <D3GanttChart
      items={items}
      title={chartTitle}
      description={items.length === 0 ? 'No scheduled dependency items to display.' : description}
      laneHeight={32}
      legendWidth={120}
      legendMode={legendMode}
      showItemLabelInBar={true}
      epochs={epochs}
      epochWidthPx={140}
      currentEpochIndex={currentEpochIndex}
      currentEpochPosition={currentEpochPosition}
      oneItemPerRow={true}
      tooltipRender={(it) => (
        <div className="px-2 py-1 rounded bg-black/80 text-white text-xs shadow">
          <div className="font-medium">{it.label}</div>
          {it.groupLabel && <div className="opacity-80">{it.groupLabel}</div>}
        </div>
      )}
    />
  );
}
